import { motion } from 'framer-motion';
import {
  Target,
  TrendingUp,
  BarChart3,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Users,
  Zap,
  DollarSign,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

const themeClasses: Record<
  string,
  { badge: string; text: string; icon: string; soft: string; tint: string; tip: string }
> = {
  blue: {
    badge: 'bg-blue-600',
    text: 'text-blue-700',
    icon: 'text-blue-600',
    soft: 'bg-blue-100',
    tint: 'bg-blue-50',
    tip: 'bg-blue-50 border-blue-500',
  },
  purple: {
    badge: 'bg-purple-600',
    text: 'text-purple-700',
    icon: 'text-purple-600',
    soft: 'bg-purple-100',
    tint: 'bg-purple-50',
    tip: 'bg-purple-50 border-purple-500',
  },
  green: {
    badge: 'bg-green-600',
    text: 'text-green-700',
    icon: 'text-green-600',
    soft: 'bg-green-100',
    tint: 'bg-green-50',
    tip: 'bg-green-50 border-green-500',
  },
  red: {
    badge: 'bg-red-600',
    text: 'text-red-700',
    icon: 'text-red-600',
    soft: 'bg-red-100',
    tint: 'bg-red-50',
    tip: 'bg-red-50 border-red-500',
  },
  orange: {
    badge: 'bg-orange-600',
    text: 'text-orange-700',
    icon: 'text-orange-600',
    soft: 'bg-orange-100',
    tint: 'bg-orange-50',
    tip: 'bg-orange-50 border-orange-500',
  },
};

export default function OkrsKpisPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Execution & Monitoring', path: '/execution-monitoring' },
              { label: 'OKRs & KPIs' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            Execution & Monitoring Tool 1 of 4
          </span>
        }
        title="Know whether your strategy is making progress"
        subtitle="Use OKRs to define the change you want and KPIs to track the health of the business. Choose measures that make review conversations clear and decisions easier."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Define your measures
        </Link>
      </PageHero>

      {/* OKRs vs KPIs */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Give ambition and business health their own measures
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              OKRs and KPIs answer different questions. Use one to focus change and the other to
              understand ongoing performance, then review them together for a fuller picture.
            </p>
          </motion.div>
          <div className="max-w-6xl mx-auto space-y-8">
            {[
              {
                type: 'OKRs (Objectives & Key Results)',
                icon: Target,
                color: 'blue',
                purpose: 'Drive transformational change and ambitious growth',
                characteristics: [
                  'Time-bound (typically quarterly)',
                  'Aspirational and challenging (60-70% achievement is success)',
                  'Focus on change and improvement',
                  'Top-down AND bottom-up',
                  'Reset each cycle',
                ],
                structure: 'Objective (qualitative) + 3-5 Key Results (quantitative)',
                example: {
                  objective: 'Become the preferred solution for SMB customers',
                  keyResults: [
                    'Increase SMB customer count from 1,200 to 2,000',
                    'Achieve 8+ NPS score from SMB segment',
                    'Reduce SMB churn rate from 18% to 12%',
                    'Launch SMB-optimized pricing tier by Q2',
                  ],
                },
                when: 'Use for strategic initiatives, growth goals, and change programs',
              },
              {
                type: 'KPIs (Key Performance Indicators)',
                icon: BarChart3,
                color: 'purple',
                purpose: 'Monitor ongoing business health and operational performance',
                characteristics: [
                  'Continuous measurement (no end date)',
                  'Target stable performance in acceptable range',
                  'Focus on maintaining and optimizing',
                  'Typically top-down',
                  'Consistent over time',
                ],
                structure: 'Metric name + Target/Threshold + Measurement frequency',
                example: {
                  objective: 'Core Business Health Metrics',
                  keyResults: [
                    'Monthly Recurring Revenue (MRR): Track monthly, target 5% MoM growth',
                    'Customer Acquisition Cost (CAC): Keep below $500',
                    'Customer Lifetime Value (LTV): Maintain >$3,000',
                    'Gross Margin: Keep above 70%',
                  ],
                },
                when: 'Use for operational health, financial metrics, and ongoing performance',
              },
            ].map((system, idx) => (
              <motion.div
                key={idx}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`public-card public-card--white p-8 rounded-xl`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`${themeClasses[system.color].badge} p-3 rounded-lg`}>
                    <system.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{system.type}</h3>
                    <p className={`text-sm ${themeClasses[system.color].text} font-medium`}>
                      {system.purpose}
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2">Key Characteristics:</p>
                    <ul className="space-y-1">
                      {system.characteristics.map((c, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <CheckCircle
                            className={`w-3 h-3 ${themeClasses[system.color].icon} mr-1.5 mt-0.5 flex-shrink-0`}
                          />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2">Structure:</p>
                    <p className="text-xs text-gray-700 mb-3">{system.structure}</p>
                    <p className="text-sm font-semibold text-gray-900 mb-2">When to Use:</p>
                    <p className="text-xs text-gray-700">{system.when}</p>
                  </div>
                </div>
                <div className={`${themeClasses[system.color].soft} p-4 rounded-lg`}>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Example:</p>
                  <p className="text-sm font-bold text-gray-900 mb-2">{system.example.objective}</p>
                  <ul className="space-y-1">
                    {system.example.keyResults.map((kr, i) => (
                      <li key={i} className="text-xs text-gray-700 flex items-start">
                        <span className={`${themeClasses[system.color].icon} mr-2`}>•</span>
                        {kr}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Write Effective OKRs */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Write Effective OKRs</h2>
              <p className="text-xl text-gray-600">
                Five-step process for creating OKRs that drive results
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Write the Objective (Qualitative)',
                  description:
                    'Start with a clear, inspiring, time-bound goal. It should be memorable and motivational. Avoid jargon. One sentence.',
                  good: '"Become the #1 rated project management tool for remote teams by Q4 2026"',
                  bad: '"Optimize our go-to-market strategy to achieve synergistic alignment" (vague, jargon-filled)',
                  tip: 'Test: Can every team member recite it? If not, too complex. Does it inspire? If not, too boring.',
                },
                {
                  step: '2',
                  title: 'Define 3-5 Key Results (Quantitative)',
                  description:
                    'Each Objective needs 3-5 measurable outcomes. Key Results are numbers—increase X from Y to Z, achieve X% of Y, launch X by date Y.',
                  good: 'KR1: Increase G2 rating from 4.2 to 4.7 (min 500 reviews), KR2: Achieve 15,000 active remote teams, KR3: Launch async collaboration features',
                  bad: 'KR: "Improve customer satisfaction" (not measurable)',
                  tip: 'Ask: "If I hit this number, did I achieve the objective?" If unclear, not a good Key Result.',
                },
                {
                  step: '3',
                  title: 'Make OKRs Ambitious (Stretch Goals)',
                  description:
                    "OKRs should be challenging. If you're confident you'll hit 100%, it's not an OKR—it's BAU (business as usual). Target 60-70% achievement.",
                  good: 'Setting revenue target 50% above normal projection to force innovation',
                  bad: 'Setting target 5% above last year (too safe, not transformational)',
                  tip: "If hitting 100% of OKRs every quarter, they're too easy. Should feel uncomfortable when setting them.",
                },
                {
                  step: '4',
                  title: 'Cascade to Teams and Individuals',
                  description:
                    'Company OKRs → Department OKRs → Team OKRs → Individual OKRs. Each level supports the level above. Everyone sees how their work connects to strategy.',
                  good: 'Company: "Expand enterprise segment" → Sales: "Close 50 enterprise deals" → Sales Rep: "Generate 200 enterprise leads"',
                  bad: "Team OKRs that don't connect to company OKRs (misalignment)",
                  tip: 'Draw the chain: Individual → Team → Department → Company. Every link should be clear. Broken chain = misalignment.',
                },
                {
                  step: '5',
                  title: 'Review Progress Weekly, Reset Quarterly',
                  description:
                    "OKRs aren't set-and-forget. Weekly check-ins: Are we on track? What's blocking us? Quarterly: Score OKRs (0.0-1.0), conduct retrospective, set next quarter.",
                  good: 'Weekly team check-in: "KR1 is 40% complete (on track), KR2 at 20% (at risk—need help)"',
                  bad: 'Setting OKRs in January, not looking at them until December',
                  tip: 'Use confidence scoring: Rate 1-10 confidence in hitting each KR. Anything <7 needs discussion now, not at quarter end.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="grid md:grid-cols-2 gap-3 mb-3">
                        <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                          <p className="text-xs font-semibold text-green-700 mb-1">
                            ✓ Good Example
                          </p>
                          <p className="text-xs text-gray-700">{item.good}</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                          <p className="text-xs font-semibold text-red-700 mb-1">✗ Bad Example</p>
                          <p className="text-xs text-gray-700">{item.bad}</p>
                        </div>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Selecting the Right KPIs */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Selecting the Right KPIs</h2>
              <p className="text-xl text-gray-600">Essential metrics by business area</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  category: 'Financial KPIs',
                  icon: DollarSign,
                  metrics: [
                    {
                      name: 'Revenue Growth Rate',
                      formula: '(Current Period Revenue - Prior Period) / Prior Period',
                      target: '15-30% YoY for growth companies',
                    },
                    {
                      name: 'Gross Margin',
                      formula: '(Revenue - COGS) / Revenue',
                      target: '>70% for SaaS, varies by industry',
                    },
                    {
                      name: 'Operating Cash Flow',
                      formula: 'Cash from operations',
                      target: 'Positive for mature, breakeven path for growth',
                    },
                    {
                      name: 'Customer Acquisition Cost (CAC)',
                      formula: 'Sales & Marketing Spend / New Customers',
                      target: 'LTV:CAC ratio of 3:1 minimum',
                    },
                  ],
                },
                {
                  category: 'Customer KPIs',
                  icon: Users,
                  metrics: [
                    {
                      name: 'Customer Lifetime Value (LTV)',
                      formula: 'Average Revenue Per Customer × Customer Lifetime',
                      target: '3x CAC minimum',
                    },
                    {
                      name: 'Net Promoter Score (NPS)',
                      formula: '% Promoters - % Detractors',
                      target: '>50 excellent, >70 world-class',
                    },
                    {
                      name: 'Customer Churn Rate',
                      formula: 'Customers Lost / Total Customers',
                      target: '<5% monthly for SaaS',
                    },
                    {
                      name: 'Customer Satisfaction (CSAT)',
                      formula: '% satisfied/very satisfied customers',
                      target: '>85%',
                    },
                  ],
                },
                {
                  category: 'Growth KPIs',
                  icon: TrendingUp,
                  metrics: [
                    {
                      name: 'Monthly Recurring Revenue (MRR)',
                      formula: 'Sum of monthly subscription revenue',
                      target: 'Track growth rate (10%+ MoM for high-growth)',
                    },
                    {
                      name: 'Annual Recurring Revenue (ARR)',
                      formula: 'MRR × 12',
                      target: 'Foundation for SaaS valuation',
                    },
                    {
                      name: 'Lead-to-Customer Conversion',
                      formula: 'New Customers / Leads',
                      target: 'Varies by industry (5-15% typical)',
                    },
                    {
                      name: 'Customer Acquisition Rate',
                      formula: 'New Customers / Month',
                      target: 'Track trend—should be accelerating',
                    },
                  ],
                },
                {
                  category: 'Operational KPIs',
                  icon: Zap,
                  metrics: [
                    {
                      name: 'Employee Productivity',
                      formula: 'Revenue / Employee',
                      target: 'Industry-dependent (SaaS: $200K+)',
                    },
                    {
                      name: 'Time to Market',
                      formula: 'Idea to launch duration',
                      target: 'Decreasing trend',
                    },
                    {
                      name: 'Project Completion Rate',
                      formula: 'Completed Projects / Total Projects',
                      target: '>90%',
                    },
                    {
                      name: 'Operating Efficiency Ratio',
                      formula: 'Operating Expenses / Revenue',
                      target: '<80% for sustainable SaaS',
                    },
                  ],
                },
              ].map((cat, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-purple-600 p-3 rounded-lg">
                      <cat.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold">{cat.category}</h3>
                  </div>
                  <div className="space-y-3">
                    {cat.metrics.map((metric, i) => (
                      <div key={i} className="bg-white p-3 rounded-lg border border-purple-200">
                        <p className="font-semibold text-sm text-gray-900 mb-1">{metric.name}</p>
                        <p className="text-xs text-gray-600 mb-1">
                          <span className="font-medium">Formula:</span> {metric.formula}
                        </p>
                        <p className="text-xs text-purple-700">
                          <span className="font-medium">Target:</span> {metric.target}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common OKR & KPI Mistakes</h2>
              <p className="text-xl text-gray-600">
                Pitfalls that undermine measurement effectiveness
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Too Many Metrics',
                  description:
                    'Tracking 50+ KPIs and 20+ OKRs. Nobody can focus on everything. Result: nothing gets attention, metrics become wallpaper.',
                  fix: 'Rule of 5: 5 company OKRs, 5 KPIs per dashboard. Focus beats comprehensiveness. Can have more metrics tracked, but only 5 get top attention.',
                },
                {
                  mistake: 'Confusing OKRs with Projects',
                  description:
                    'Writing "Launch new feature" as Key Result. That\'s a project/initiative, not an outcome. OKRs measure impact, not activity.',
                  fix: 'Ask: "If we launch the feature but get no adoption, did we succeed?" If answer is no, measure adoption (outcome), not launch (output).',
                },
                {
                  mistake: "Setting OKRs You're 100% Confident In",
                  description:
                    "OKRs that feel comfortable and safe. If you're sure you'll hit them, they're not stretch goals—they're commitments masquerading as OKRs.",
                  fix: 'OKRs should feel 50-70% achievable when set. Uncomfortable but possible. If hitting 100% every quarter, increase ambition.',
                },
                {
                  mistake: 'Never Updating KPIs',
                  description:
                    "Using same KPIs for 5 years as business evolves. What mattered as startup doesn't matter at scale. Outdated KPIs drive wrong behavior.",
                  fix: 'Annual KPI review: Are these still the right metrics? Do they reflect current strategy? Retire old, add new. KPIs should evolve with business.',
                },
                {
                  mistake: 'No Clear Ownership',
                  description:
                    'OKRs/KPIs owned by "the team" or "everyone." Result: no one accountable. Metrics drift without improvement.',
                  fix: "Every OKR and top-level KPI must have single owner (name, not role). They're accountable for results and improvement plans.",
                },
                {
                  mistake: 'Vanity Metrics Over Action Metrics',
                  description:
                    "Measuring page views instead of conversions, signups instead of activation, downloads instead of daily active users. Metrics that look good but don't correlate with success.",
                  fix: 'For each metric, ask: "If this improves but revenue/profit doesn\'t, is that success?" If no, it\'s vanity. Find the metric that actually drives business outcomes.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Create measures that support a useful conversation
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Define the change</h3>
                <p className="text-gray-600">
                  Write a focused objective and key results that describe observable progress.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Watch business health</h3>
                <p className="text-gray-600">
                  Choose KPIs with a clear definition, source, and reason for tracking them.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Review and respond</h3>
                <p className="text-gray-600">
                  Use the evidence to discuss what is working, what is blocked, and what should
                  change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Ensure OKRs align with strategic objectives
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/strategic-cadence"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Strategic Cadence</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Establish review rhythms for OKRs and KPIs
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/accountability-ownership"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Users className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">
                  Accountability & Ownership
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assign clear ownership for every OKR and KPI
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/execution-monitoring"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Back to Execution & Monitoring
              </Link>
              <Link
                to="/execution-monitoring/strategic-cadence"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Strategic Cadence <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Define your measures in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Use the evidence to discuss what is working, what is blocked, and what should change.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Define your measures <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
